import { ChangeEvent, FC, useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { demeterUserDataApi } from '../../../Apis/Apis';
import arrowLeft from '../../../Components/Assets/arrowLeft.svg';
import arrowRight from '../../../Components/Assets/arrowRight.svg';
import ContentType from '../../../Core/Common/ContentTypes';
import FileExtension from '../../../Core/Common/FileExtensions';
import { GetDemeterUserDataFileResponse, UpdateDemeterUserDataRequest } from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApi from '../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import ComponentSubHeader from '../../Components/Headers/ComponentSubHeader';
import PageLoadingSpinner from '../../Components/LoadingSpinner/PageLoadingSpinner';
import ConfirmModal from '../../Components/Modals/ConfirmModal/ConfirmModal';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import ActionsCellButton from '../../Components/Tables/ActionsCellButton/ActionsCellButton';
import browserService from '../../Services/BrowserService';
import useLanguage from '../../Services/Language/useLanguageHook';
import { GetDemeterUserDataFileRequest, IUserData } from './UserDataDefinitions';
import UserDataForm from './UserDataForm';
import styles from './UserDataPage.module.scss';
import UserDataStartup from './UserDataStartup';

const defaultRowsDisplayPerPage = 25;

const UserDataManagement: FC = () => {
    const [translations] = useLanguage();
    const navigate = useNavigate();
    const [data, setData] = useState<IUserData[]>();
    const [value, setValue] = useState<IUserData>();
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsDisplayPerPage);
    const [showLoading, setShowLoading] = useState(true);
    const [, setSearchParameters] = useSearchParameters();
    const totalPages = (data && Math.ceil(data.length / rowsPerPage)) ?? 0;

    const [, listDemeterUserData, listDemeterUserDataResponse] = useApi(() => demeterUserDataApi.listDemeterUserDataCurrent());

    const [, getDemeterUserDataFile, getDemeterUserDataFileResponse] = useApiWithoutAutoExecute<GetDemeterUserDataFileRequest, GetDemeterUserDataFileResponse>(
        (request: GetDemeterUserDataFileRequest | undefined) => {
            if (!request) {
                return null;
            }

            return demeterUserDataApi.getDemeterUserDataFile(request.demeterUserDataGuid, request.contentType);
        },
    );

    const [, updateDemeterUserData, updateDemeterUserDataResponse] = useApi(() => {
        if (!value) {
            return null;
        }

        return demeterUserDataApi.updateDemeterUserData(value.id, {
            name: value.name,
            fileName: value.fileName,
            currency: value.currency,
            unitOfMeasure: value.unitOfMeasure,
        } as UpdateDemeterUserDataRequest);
    });

    const [, deleteDemeterUserData, deleteDemeterUserDataResponse] = useApiWithoutAutoExecute(() => {
        if (!value) {
            return null;
        }
        return demeterUserDataApi.deleteDemeterUserData(value.id!);
    });

    useEffect(() => {
        if (!listDemeterUserDataResponse) {
            return;
        }
        const userData = listDemeterUserDataResponse.rows?.map(
            (x) =>
                ({
                    id: x.demeterUserDataGuid,
                    name: x.name,
                    fileName: x.fileName,
                    currency: x.currency,
                    unitOfMeasure: x.unitOfMeasure,
                    data: [],
                } as IUserData),
        );
        setData(userData);
        setShowLoading(false);
    }, [listDemeterUserDataResponse]);

    useEffect(() => {
        if (!getDemeterUserDataFileResponse) {
            return;
        }
        browserService.downloadFile(
            getDemeterUserDataFileResponse.fileContent!,
            getDemeterUserDataFileResponse.fileName!,
            getDemeterUserDataFileResponse.contentType!,
        );
    }, [getDemeterUserDataFileResponse]);

    useEffect(() => {
        if (!updateDemeterUserDataResponse) {
            return;
        }
        listDemeterUserData();
        setFormDialogOpen(false);
        setValue(undefined);
    }, [updateDemeterUserDataResponse]);

    useEffect(() => {
        if (!deleteDemeterUserDataResponse) {
            return;
        }
        setShowLoading(false);
        setValue(undefined);
        listDemeterUserData();
    }, [deleteDemeterUserDataResponse]);

    const handleChangePage = (newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLSelectElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChange = (dataItem: IUserData) => {
        setValue(dataItem);
        updateDemeterUserData();
    };

    const handleClose = () => {
        setFormDialogOpen(false);
        setValue(undefined);
    };

    const handleDelete = (dataItem: IUserData) => {
        setValue(dataItem);
        setConfirmDeleteModalOpen(true);
    };

    const handleDeleteConfirmed = () => {
        setConfirmDeleteModalOpen(false);
        deleteDemeterUserData();
    };

    const handleDownload = (dataItem: IUserData) => {
        if (dataItem.fileName.endsWith(FileExtension.Xlsx)) {
            getDemeterUserDataFile({ demeterUserDataGuid: dataItem.id, contentType: ContentType.Excel });
        } else {
            getDemeterUserDataFile({ demeterUserDataGuid: dataItem.id, contentType: ContentType.Csv });
        }
    };

    const handleUpload = () => {
        setSearchParameters({ show: 'Upload' });
    };

    const showStartup = !showLoading && (!data || data.length === 0);
    const showManagement = !showLoading && !showStartup;

    return (
        <>
            {showLoading && <PageLoadingSpinner />}
            {showStartup && <UserDataStartup />}
            {showManagement && (
                <div className={styles.master_page_container} data-track="Management">
                    <div className={styles.user_data_management}>
                        <div className={styles.user_data_management_header_container}>
                            <h2>{translations.userData.management.header}</h2>
                            <LinkButton
                                title={translations.userData.management.upload}
                                onClick={handleUpload}
                                type={LinkButtonType.White}
                                disabled={showLoading}
                                track={{ eventCategory: 'UserData', eventAction: 'UploadClicked' }}
                            />
                        </div>
                        <div className={styles.user_data_management_container}>
                            <div className={styles.user_data_management_description}>{translations.userData.management.description}</div>
                            <div>
                                <ComponentSubHeader title={translations.userData.management.subheader} />
                                <div className={styles.user_data_management_table}>
                                    <div className={styles.user_data_management_table_row}>
                                        <div className={styles.user_data_management_table_header_cell}>{translations.userData.management.table.nameHeader}</div>
                                        <div className={styles.user_data_management_table_header_cell}>
                                            {translations.userData.management.table.fileNameHeader}
                                        </div>
                                        <div className={styles.user_data_management_table_header_cell}>{translations.userData.management.table.unitHeader}</div>
                                        <div className={styles.user_data_management_table_header_cell}>
                                            {translations.userData.management.table.currencyHeader}
                                        </div>
                                        <div className={styles.user_data_management_table_header_cell}>
                                            {translations.userData.management.table.actionsHeader}
                                        </div>
                                    </div>
                                    {data &&
                                        data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((dataItem) => (
                                            <div key={dataItem.id} className={styles.user_data_management_table_row}>
                                                <div className={styles.user_data_management_table_cell}>{dataItem.name}</div>
                                                <div className={styles.user_data_management_table_cell}>{dataItem.fileName}</div>
                                                <div className={styles.user_data_management_table_cell}>
                                                    {translations.unitOfMeasure[dataItem.unitOfMeasure]}
                                                </div>
                                                <div className={styles.user_data_management_table_cell}>{translations.currency[dataItem.currency]}</div>
                                                <div className={styles.user_data_management_table_cell}>
                                                    <div className={styles.user_data_management_table_actions}>
                                                        <ActionsCellButton
                                                            text={translations.actions.delete}
                                                            handleClick={() => handleDelete(dataItem)}
                                                            track={{ eventCategory: 'UserData', eventAction: 'DeleteClicked' }}
                                                        />
                                                        <ActionsCellButton
                                                            text={translations.actions.edit}
                                                            handleClick={() =>
                                                                navigate(generatePath(NavigationRoutes.UserDataItems, { demeterUserDataGuid: dataItem.id }))
                                                            }
                                                            track={{ eventCategory: 'UserData', eventAction: 'EditClicked' }}
                                                        />
                                                        <ActionsCellButton
                                                            text={translations.actions.download}
                                                            handleClick={() => handleDownload(dataItem)}
                                                            track={{ eventCategory: 'UserData', eventAction: 'DownloadClicked' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                <div className={styles.user_data_management_table_footer}>
                                    <div>{translations.userData.management.table.itemsPerPage}:</div>
                                    <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                                        {[5, 10, 25].map((rowsPerPageOption) => (
                                            <option key={rowsPerPageOption} value={rowsPerPageOption}>
                                                {rowsPerPageOption}
                                            </option>
                                        ))}
                                    </select>
                                    <div>
                                        {page + 1} {translations.userData.management.table.pageOf} {totalPages}
                                    </div>
                                    <button type="button" onClick={() => handleChangePage(Math.max(0, page - 1))} disabled={page === 0}>
                                        <img src={arrowLeft} alt="" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleChangePage(Math.min(totalPages - 1, page + 1))}
                                        disabled={page >= totalPages - 1}
                                    >
                                        <img src={arrowRight} alt="" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <UserDataForm open={formDialogOpen} value={value} onClose={handleClose} onChange={handleChange} />
            <ConfirmModal
                header={translations.userData.management.confirmDelete.header}
                message={translations.userData.management.confirmDelete.message}
                showModal={confirmDeleteModalOpen}
                handleConfirm={handleDeleteConfirmed}
                handleCancel={() => setConfirmDeleteModalOpen(false)}
                track={{ eventCategory: 'UserData', eventAction: 'DeleteConfirm' }}
            />
        </>
    );
};
export default UserDataManagement;
